import React from 'react';
import PropTypes from 'prop-types';

// Import all enhancers that are used inside the application
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';

// Translations.

// Fetcher hook.
import connectFetcher from '@exonet/api-fetcher-client';
import connectSocket from '@exonet/api-socket-client/lib/hoc';

// React-shared.
import { Loader as ModalLoader } from '@exonet/react-shared/lib/Elements/Modal';

// Loader component.
import SharedLoader from '@exonet/react-shared/lib/Elements/Loader';
import i18next from 'app/client/utilities/i18n';
import { rejectFailedRequests } from 'app/client/utilities/fetcher';

// Local components.
import ApiError from 'app/client/components/ApiError';
import MissingResource from 'app/client/components/MissingResource';
import Restricted from 'app/client/components/Restricted';
import withRouter from '../withRouter';

/**
 * Boost the component with the given settings.
 *
 * @param {Object|boolean} fetcher The fetcher settings or false.
 * @param {Object|boolean} socket The socket settings or false.
 * @param {Object|boolean} store The store settings or false.
 * @param {Object|array|boolean} i18n The i18n settings or false.
 * @param {boolean} router Whether or not to wrap the withRouter HOC.
 * @param {boolean} modal If true then a modal loader is used.
 * @param {Object|boolean} resource The store prop (`name`) without which the page renders as not
 *   found, the `list` and `title` to send the user back to, and the `permissions` the page needs.
 *   Any non-fatal status on it counts as not found, a 401 too until the session reloads.
 *
 * @returns {*} The wrapped element.
 */
export default ({
    fetcher = false,
    socket = false,
    store = false,
    i18n = false,
    router = false,
    modal = false,
    loader = false,
    resource = false,
  }) =>
  (WrappedComponent) => {
    if (i18n !== false) {
      const translations = Array.isArray(i18n) ? i18n : [i18n];
      translations.forEach((translation) => {
        i18next.addResourceBundle('en', translation.namespace, translation.en);
        i18next.addResourceBundle('nl', translation.namespace, translation.nl);
      });
    }

    const boosters = [
      router !== false ? withRouter : null,
      i18n !== false ? withTranslation() : null,
      fetcher !== false
        ? connectFetcher(
            rejectFailedRequests(fetcher),
            loader || (modal ? ModalLoader : SharedLoader),
            ApiError,
          )
        : null,
      socket !== false ? connectSocket(socket) : null,
      store !== false ? connect(...store) : null,
      resource !== false
        ? (Component) => {
            const WithResource = (props) => {
              const skeleton = loader && React.createElement(loader, props);

              if (props[resource.name]) {
                return <Component {...props} />;
              }

              // Not missing but gone is a failed refresh, which the layout shows in its banner.
              if (!props.missing) {
                return skeleton;
              }

              return (
                <Restricted to={resource.permissions}>
                  <MissingResource
                    skeleton={skeleton}
                    list={resource.list}
                    title={resource.title}
                  />
                </Restricted>
              );
            };

            WithResource.propTypes = { missing: PropTypes.bool.isRequired };

            return WithResource;
          }
        : null,
    ].filter((booster) => booster !== null);

    // Boost the component.
    return compose(...boosters)(WrappedComponent);
  };
